import React from 'react';

/**
 * KnowledgeCheckView - Mid-lesson multiple choice quiz
 *
 * Modern, compact UI optimized for large screens.
 * Renders questions as a responsive 2-column grid of elevated panels so a
 * typical 4-question check is fully visible without scrolling; collapses
 * to a single scrollable column on narrower viewports (see the
 * `.kc-questions-grid` media queries below).
 */
export default function KnowledgeCheckView({
  // Status
  status,
  error,
  isLoading,
  isSubmitting,

  // Data
  questions,
  previousAttempt,

  // User input
  answers,
  setAnswer,
  allAnswered,

  // Results
  results,
  score,
  total,
  passed,

  // Actions
  initialize,
  submit,
  retry,

  // Navigation
  onContinue,
}) {
  const showResults = status === 'results';
  const isReady = status === 'ready';

  // Get result for a specific question
  const getResult = (questionId) => {
    if (!results) return null;
    return results.find(r => r.questionId === questionId);
  };

  return (
    <>
      <style>{`
        @keyframes kcFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .kc-container {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
          max-width: var(--frame-dashboard);
          margin: 0 auto;
          padding: var(--space-5) var(--space-6);
          box-sizing: border-box;
        }

        .kc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-5);
          animation: kcFadeIn 0.3s ease;
          flex-shrink: 0;
        }

        .kc-header-left {
          display: flex;
          align-items: center;
          gap: var(--space-4);
        }

        .kc-badge-group {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
        }

        /* Category label, not a progress/completion signal — neutral,
           like course-card-chip (spec §2: green is reserved for
           progress/completion/current-location/the one primary action). */
        .kc-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
          padding: var(--space-1) var(--space-3);
          background: var(--bg-tertiary);
          border-radius: var(--radius-full);
        }

        .kc-badge-icon {
          width: 16px;
          height: 16px;
          color: var(--text-secondary);
        }

        .kc-badge-text {
          font-size: var(--text-label-size);
          font-weight: var(--text-label-weight);
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: var(--text-label-tracking);
        }

        /* Spec 1c's "Not graded" secondary note, folded into the existing
           page-level header (kc-badge-text already carries the spec's
           uppercase tracked label styling) instead of a separate per-panel
           label — the spec's inline instance has no page header of its
           own, so this component's single header is the one place that
           label belongs. */
        .kc-header-subtext {
          font-size: var(--text-numeric-size);
          color: var(--text-tertiary);
        }

        .kc-title {
          font-size: var(--text-section-size);
          font-weight: var(--text-section-weight);
          color: var(--text-primary);
          margin: 0;
          letter-spacing: var(--text-section-tracking);
        }

        .kc-subtitle {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0;
        }

        .kc-header-right {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }

        .kc-progress {
          font-size: var(--text-body-size);
          color: var(--text-tertiary);
        }

        /* Numeric progress reads as a number, not a colored accent — matches
           course-card-pct's neutral treatment of progress text. */
        .kc-progress-count {
          font-weight: 600;
          color: var(--text-primary);
        }

        .kc-passed-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
          padding: var(--space-1) var(--space-3);
          background: var(--success-bg);
          border: 1px solid var(--success);
          border-radius: var(--radius-full);
          font-size: var(--text-label-size);
          font-weight: var(--text-label-weight);
          color: var(--success);
        }

        .kc-passed-badge svg {
          width: 14px;
          height: 14px;
        }

        /* Wide 2-column card grid so a typical 4-question check fits one
           viewport without scrolling (auto-flow handles other counts —
           3 wraps to a partial second row, 5+ grows more rows).
           overflow-y stays as a safety net for tall content, but the goal
           is to never need it. */
        .kc-questions-grid {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          grid-auto-rows: 1fr;
          gap: var(--space-4);
          align-items: stretch;
          overflow-y: auto;
          padding-bottom: var(--space-4);
        }

        .kc-question-card {
          background: var(--bg-elevated);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-panel);
          padding: var(--space-4);
          animation: kcFadeIn 0.3s ease backwards;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* Correct is a completion signal, so green is legitimate here (spec
           §2 nuance). Flat token wash, not a gradient — CLAUDE.md bans
           gradients; the redesign favors clean surfaces. */
        .kc-question-card.correct {
          border-color: var(--success);
          background: var(--success-bg);
        }

        .kc-question-card.incorrect {
          border-color: var(--danger);
          background: var(--danger-bg);
        }

        .kc-question-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-3);
        }

        .kc-question-number {
          font-size: var(--text-label-size);
          font-weight: var(--text-label-weight);
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: var(--text-label-tracking);
        }

        .kc-question-status {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .kc-question-status.correct {
          background: var(--success);
        }

        .kc-question-status.incorrect {
          background: var(--danger);
        }

        .kc-question-text {
          font-size: var(--text-panel-size);
          font-weight: var(--text-panel-weight);
          color: var(--text-primary);
          line-height: 1.4;
          margin: 0 0 var(--space-3) 0;
          overflow-wrap: anywhere;
        }

        .kc-options {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          flex: 1;
        }

        .kc-option {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3);
          background: transparent;
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-control);
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: left;
          width: 100%;
        }

        .kc-option:hover:not(.disabled) {
          background: var(--glass-bg-medium);
          border-color: var(--border-hover);
        }

        /* An ungraded selection is not a status — it must not read as
           "correct" before submission. Neutral emphasis only (border
           weight + background), never green: green in this component
           means graded-correct, full stop. */
        .kc-option.selected {
          background: var(--glass-bg-medium);
          border-color: var(--border-strong);
        }

        .kc-option.correct-answer {
          background: var(--success-bg);
          border-color: var(--success);
        }

        .kc-option.wrong-answer {
          background: var(--danger-bg);
          border-color: var(--danger);
        }

        .kc-option.disabled {
          cursor: default;
        }

        .kc-option-radio {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid var(--border-subtle);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.15s ease;
        }

        .kc-option.selected .kc-option-radio {
          border-color: var(--text-primary);
          background: var(--text-primary);
        }

        .kc-option.correct-answer .kc-option-radio {
          border-color: var(--success);
          background: var(--success);
        }

        .kc-option.wrong-answer .kc-option-radio {
          border-color: var(--danger);
          background: var(--danger);
        }

        .kc-option-radio-inner {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--text-inverted);
          transform: scale(0);
          transition: transform 0.1s ease;
        }

        .kc-option.selected .kc-option-radio-inner,
        .kc-option.correct-answer .kc-option-radio-inner,
        .kc-option.wrong-answer .kc-option-radio-inner {
          transform: scale(1);
        }

        .kc-option-letter {
          font-size: var(--text-numeric-size);
          font-weight: 700;
          color: var(--text-tertiary);
          min-width: 16px;
        }

        .kc-option.selected .kc-option-letter {
          color: var(--text-primary);
        }

        .kc-option.correct-answer .kc-option-letter {
          color: var(--success);
        }

        .kc-option.wrong-answer .kc-option-letter {
          color: var(--danger);
        }

        .kc-option-text {
          font-size: var(--text-body-size);
          color: var(--text-primary);
          line-height: 1.4;
          flex: 1;
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .kc-option-icon {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        /* Neutral divider accent, not the brand green — this is a
           decorative callout rule, not a progress/completion signal
           (spec §2). */
        .kc-explanation {
          margin-top: var(--space-3);
          padding: var(--space-3);
          background: var(--glass-bg-light);
          border-radius: var(--radius-control);
          border-left: 2px solid var(--border-strong);
        }

        .kc-explanation-text {
          font-size: var(--text-numeric-size);
          color: var(--text-secondary);
          line-height: 1.5;
          margin: 0;
        }

        .kc-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: var(--space-4);
          border-top: 1px solid var(--border-subtle);
          margin-top: auto;
          flex-shrink: 0;
        }

        .kc-score-inline {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }

        .kc-score-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .kc-score-icon.passed {
          background: var(--success);
        }

        .kc-score-icon.failed {
          background: var(--warning);
        }

        .kc-score-icon svg {
          width: 18px;
          height: 18px;
          color: var(--text-inverted);
        }

        .kc-score-text h3 {
          font-size: var(--text-panel-size);
          font-weight: var(--text-panel-weight);
          color: var(--text-primary);
          margin: 0;
        }

        .kc-score-text p {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0;
        }

        .kc-actions {
          display: flex;
          gap: var(--space-2);
        }

        .kc-btn {
          padding: var(--space-3) var(--space-5);
          border-radius: var(--radius-control);
          font-size: var(--text-body-size);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
        }

        .kc-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .kc-btn-primary {
          background: var(--btn-primary-bg);
          color: var(--btn-primary-text);
          border: none;
        }

        .kc-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .kc-btn-secondary {
          background: var(--glass-bg-medium);
          color: var(--text-primary);
          border: 1px solid var(--border-subtle);
        }

        .kc-btn-secondary:hover:not(:disabled) {
          background: var(--glass-bg-light);
          border-color: var(--border-hover);
        }

        /* Centered states */
        .kc-centered {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          animation: kcFadeIn 0.3s ease;
        }

        .kc-spinner {
          width: 32px;
          height: 32px;
          border: 2px solid var(--border-subtle);
          border-top-color: var(--accent-primary);
          border-radius: 50%;
          animation: graspd-spin 0.8s linear infinite;
        }

        /* Illustrative icon for the idle/start state — neutral, not the
           brand green (spec §2: decorative, not a progress/completion
           signal). */
        .kc-centered-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--bg-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: var(--space-4);
        }

        .kc-centered-icon svg {
          width: 32px;
          height: 32px;
          color: var(--text-secondary);
        }

        .kc-centered-icon.error {
          background: var(--danger-bg);
        }

        .kc-centered-icon.error svg {
          color: var(--danger);
        }

        .kc-centered-title {
          font-size: var(--text-section-size);
          font-weight: var(--text-section-weight);
          color: var(--text-primary);
          margin: 0 0 var(--space-2) 0;
        }

        .kc-centered-desc {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0 0 var(--space-5) 0;
          max-width: 320px;
        }

        .kc-previous-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
          padding: var(--space-1) var(--space-3);
          background: var(--glass-bg-medium);
          border-radius: var(--radius-full);
          margin-bottom: var(--space-4);
          font-size: var(--text-label-size);
          color: var(--text-secondary);
        }

        .kc-previous-badge.passed {
          background: var(--success-bg);
          color: var(--success);
        }

        /* Below ~900px there isn't enough room for two comfortably-wide
           columns (long option text starts wrapping awkwardly) — collapse
           to one column and let the grid scroll like before. */
        @media (max-width: 900px) {
          .kc-questions-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .kc-container {
            padding: var(--space-4);
            padding-bottom: calc(var(--space-6) * 3); /* Clear fixed footer */
          }

          .kc-header {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--space-3);
            margin-bottom: var(--space-4);
          }

          .kc-header-left {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--space-2);
          }

          .kc-title {
            font-size: var(--text-section-size);
          }

          .kc-questions-grid {
            grid-template-columns: 1fr;
            gap: var(--space-3);
          }

          .kc-question-card {
            padding: var(--space-3);
          }

          .kc-question-text {
            font-size: var(--text-body-size);
          }

          .kc-option {
            padding: var(--space-3);
          }

          .kc-option-text {
            font-size: var(--text-body-size);
          }

          .kc-footer {
            flex-direction: column;
            gap: var(--space-3);
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: var(--bg-primary);
            border-top: 1px solid var(--border-subtle);
            padding: var(--space-3) var(--space-4);
            padding-bottom: max(var(--space-3), env(safe-area-inset-bottom));
            margin-top: 0;
            z-index: 150;
          }

          .kc-score-inline {
            width: 100%;
          }

          .kc-actions {
            width: 100%;
          }

          .kc-actions .kc-btn {
            flex: 1;
            justify-content: center;
          }

          .kc-centered {
            padding: var(--space-5) var(--space-4);
          }

          .kc-centered-title {
            font-size: var(--text-section-size);
          }

          .kc-centered-desc {
            font-size: var(--text-body-size);
          }
        }
      `}</style>

      <div className="kc-container">
        {/* Loading State */}
        {isLoading && (
          <div className="kc-centered">
            <div className="kc-spinner" />
            <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-size)', marginTop: 'var(--space-3)' }}>
              Loading...
            </span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="kc-centered">
            <div className="kc-centered-icon error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 className="kc-centered-title">Something went wrong</h2>
            <p className="kc-centered-desc">{error}</p>
            <button className="kc-btn kc-btn-primary" onClick={initialize}>
              Try Again
            </button>
          </div>
        )}

        {/* Idle State - Start Card */}
        {status === 'idle' && !error && !isLoading && (
          <div className="kc-centered">
            <div className="kc-centered-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12l2 2 4-4" />
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.5 0 2.91.37 4.15 1.02" />
              </svg>
            </div>
            <h2 className="kc-centered-title">Knowledge Check</h2>
            <p className="kc-centered-desc">
              Test your understanding with a few quick questions.
            </p>
            {previousAttempt && (
              <div className={`kc-previous-badge ${previousAttempt.passed ? 'passed' : ''}`}>
                {previousAttempt.passed ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Passed ({previousAttempt.score}/{previousAttempt.total})
                  </>
                ) : (
                  <>Previous: {previousAttempt.score}/{previousAttempt.total}</>
                )}
              </div>
            )}
            <button className="kc-btn kc-btn-primary" onClick={initialize}>
              {previousAttempt ? 'Retake' : 'Start'}
            </button>
          </div>
        )}

        {/* Questions View */}
        {(isReady || showResults) && questions.length > 0 && (
          <>
            <div className="kc-header">
              <div className="kc-header-left">
                <div className="kc-badge-group">
                  <div className="kc-badge">
                    <svg className="kc-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9 12l2 2 4-4" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    <span className="kc-badge-text">Knowledge Check</span>
                  </div>
                  {!showResults && <span className="kc-header-subtext">Not graded</span>}
                </div>
                <h1 className="kc-title">
                  {showResults
                    ? (passed ? 'All Correct!' : 'Review & Retry')
                    : (previousAttempt?.passed ? 'Review Your Answers' : 'Answer All Questions')
                  }
                </h1>
              </div>
              <div className="kc-header-right">
                {!showResults && previousAttempt?.passed && (
                  <div className="kc-passed-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Passed ({previousAttempt.score}/{previousAttempt.total})
                  </div>
                )}
                {!showResults && !previousAttempt?.passed && (
                  <span className="kc-progress">
                    <span className="kc-progress-count">{answers.filter(a => a >= 0).length}</span>
                    /{questions.length} answered
                  </span>
                )}
                {showResults && (
                  <span className="kc-subtitle">
                    {score}/{total} correct
                  </span>
                )}
              </div>
            </div>

            <div className="kc-questions-grid">
              {questions.map((q, qIndex) => {
                const result = getResult(q.id);
                const isCorrect = result?.correct;
                const cardClass = showResults
                  ? (isCorrect ? 'correct' : 'incorrect')
                  : '';

                return (
                  <div
                    key={q.id}
                    className={`kc-question-card ${cardClass}`}
                    style={{ animationDelay: `${qIndex * 0.05}s` }}
                  >
                    <div className="kc-question-header">
                      <span className="kc-question-number">Q{qIndex + 1}</span>
                      {showResults && (
                        <div className={`kc-question-status ${isCorrect ? 'correct' : 'incorrect'}`}>
                          {isCorrect ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-inverted)" strokeWidth="1.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-inverted)" strokeWidth="1.5">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="kc-question-text">{q.question}</p>

                    <div className="kc-options">
                      {q.options.map((option, oIndex) => {
                        const isSelected = answers[qIndex] === oIndex;
                        const isCorrectAnswer = showResults && result?.correctIndex === oIndex;
                        const isWrongAnswer = showResults && isSelected && !isCorrect;

                        let optionClass = '';
                        if (showResults) {
                          if (isCorrectAnswer) optionClass = 'correct-answer';
                          else if (isWrongAnswer) optionClass = 'wrong-answer';
                          optionClass += ' disabled';
                        } else if (isSelected) {
                          optionClass = 'selected';
                        }

                        return (
                          <button
                            key={oIndex}
                            type="button"
                            className={`kc-option ${optionClass}`}
                            onClick={() => !showResults && setAnswer(qIndex, oIndex)}
                            disabled={showResults}
                          >
                            <div className="kc-option-radio">
                              <div className="kc-option-radio-inner" />
                            </div>
                            <span className="kc-option-letter">
                              {String.fromCharCode(65 + oIndex)}
                            </span>
                            <span className="kc-option-text">{option}</span>
                            {showResults && isCorrectAnswer && (
                              <svg className="kc-option-icon" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="1.5">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                            {showResults && isWrongAnswer && (
                              <svg className="kc-option-icon" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="1.5">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {showResults && result?.explanation && (
                      <div className="kc-explanation">
                        <p className="kc-explanation-text">{result.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="kc-footer">
              <div className="kc-score-inline">
                {showResults && (
                  <>
                    <div className={`kc-score-icon ${passed ? 'passed' : 'failed'}`}>
                      {passed ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <rect x="10.5" y="5" width="3" height="10" rx="1.5" />
                          <circle cx="12" cy="18" r="2" />
                        </svg>
                      )}
                    </div>
                    <div className="kc-score-text">
                      <h3>{passed ? 'Passed!' : 'Not quite'}</h3>
                      <p>{passed ? 'Continue to next section' : 'Review and try again'}</p>
                    </div>
                  </>
                )}
                {isReady && previousAttempt?.passed && (
                  <>
                    <div className="kc-score-icon passed">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div className="kc-score-text">
                      <h3>Already Passed</h3>
                      <p>You can continue or review your answers</p>
                    </div>
                  </>
                )}
              </div>

              <div className="kc-actions">
                {showResults && !passed && (
                  <button className="kc-btn kc-btn-secondary" onClick={retry}>
                    Try Again
                  </button>
                )}
                {showResults && (
                  <button className="kc-btn kc-btn-primary" onClick={() => onContinue(passed)}>
                    Continue
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                )}
                {isReady && !previousAttempt?.passed && (
                  <button
                    className="kc-btn kc-btn-primary"
                    onClick={submit}
                    disabled={!allAnswered || isSubmitting}
                  >
                    {isSubmitting ? 'Checking...' : 'Submit'}
                  </button>
                )}
                {isReady && previousAttempt?.passed && (
                  <>
                    <button
                      className="kc-btn kc-btn-secondary"
                      onClick={submit}
                      disabled={!allAnswered || isSubmitting}
                    >
                      {isSubmitting ? 'Checking...' : 'Resubmit'}
                    </button>
                    <button className="kc-btn kc-btn-primary" onClick={() => onContinue(true)}>
                      Continue
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
